/** Cart / collection product helpers & PDF field extraction */

export function getProductImageSrc(product) {
  if (!product?.images || !Array.isArray(product.images)) return null;
  const main =
    product.images.find((img) => img?.isMain || img?.is_main) ||
    product.images[0];
  if (!main) return null;
  if (main.data) return main.data;
  if (main.path) return main.path;
  return null;
}

export function formatNamedRelationList(value) {
  if (value == null || value === '') return null;
  if (typeof value === 'string') return value;
  if (!Array.isArray(value)) return String(value);
  if (value.length === 0) return null;
  const parts = value
    .map((item) => {
      if (item != null && typeof item === 'object' && item.name != null)
        return item.name;
      return item != null ? String(item) : '';
    })
    .filter(Boolean);
  return parts.length ? parts.join(', ') : null;
}

export function escapeHtml(s) {
  if (s == null || s === '') return '';
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

export function formatDimensionsPdf(product) {
  const l = product.lengthDimension ?? product.length_dimension;
  const b = product.breadthDimension ?? product.breadth_dimension;
  const h = product.heightDimension ?? product.height_dimension;
  const u = (product.dimensionUnit ?? product.dimension_unit ?? '').trim();
  if (l != null && b != null && h != null) {
    const lf = Number(l);
    const bf = Number(b);
    const hf = Number(h);
    const ls = Number.isFinite(lf) ? lf.toFixed(2) : String(l);
    const bs = Number.isFinite(bf) ? bf.toFixed(2) : String(b);
    const hs = Number.isFinite(hf) ? hf.toFixed(2) : String(h);
    return `${ls} x ${bs} x ${hs}${u ? ` ${u}` : ''}`;
  }
  return '—';
}

export function formatNetWeightPdf(product) {
  const w = product.netWeight ?? product.net_weight;
  const unit = (product.weightUnit ?? product.weight_unit ?? '').trim();
  if (w != null && w !== '') {
    const n = Number(w);
    const ws = Number.isFinite(n) ? n.toFixed(3) : String(w);
    return unit ? `${ws} ${unit}` : ws;
  }
  return '—';
}

export function formatPricePdf(product, currencySymbol) {
  const c =
    product.cost ??
    product.price ??
    product.unitPrice ??
    product.unit_price;
  if (c == null || c === '') return '—';
  const n = Number(c);
  const val = Number.isFinite(n) ? n.toFixed(2) : String(c);
  return `${currencySymbol}${val}`;
}

export function normalizePackArray(raw) {
  if (raw == null) return null;
  if (Array.isArray(raw)) return raw;
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : null;
    } catch {
      return null;
    }
  }
  return null;
}

export function summarizePackRows(packs) {
  const arr = normalizePackArray(packs);
  if (!Array.isArray(arr) || arr.length === 0) return null;
  const lines = arr
    .map((row) => {
      if (!row || typeof row !== 'object') return null;
      const q = row.quantityPack ?? row.quantity_pack ?? row.unit;
      const l = row.length;
      const b = row.breadth;
      const h = row.height;
      const nw = row.netWt ?? row.net_weight;
      const parts = [];
      if (q !== '' && q != null) parts.push(`Qty/pack: ${q}`);
      const hasDims = [l, b, h].some((x) => x !== '' && x != null);
      if (hasDims) {
        parts.push(
          `L × W × H: ${l !== '' && l != null ? l : '—'} × ${b !== '' && b != null ? b : '—'} × ${h !== '' && h != null ? h : '—'}`,
        );
      }
      if (nw !== '' && nw != null) parts.push(`Net wt: ${nw}`);
      return parts.length ? parts.join(' · ') : null;
    })
    .filter(Boolean);
  return lines.length ? lines : null;
}

export function packLinesHtml(packs) {
  const lines = summarizePackRows(packs);
  if (!lines || lines.length === 0) return '—';
  return lines.map((line) => escapeHtml(line)).join('<br/>');
}

export function pdfSkuRaw(p) {
  const v = p.itemNo ?? p.item_no ?? p.sku ?? p.skuCode;
  return v != null && String(v).trim() !== '' ? String(v).trim() : '';
}

export function pdfNameRaw(p) {
  const v =
    p.productName ?? p.product_name ?? p.name ?? p.description ?? p.title;
  if (v != null && typeof v === 'object') {
    if (v.name != null) return String(v.name);
    return '';
  }
  return v != null && String(v).trim() !== '' ? String(v).trim() : '';
}

export function pdfHsnRaw(p) {
  const raw = p.hsCode ?? p.hs_code ?? p.HSN ?? p.hsn;
  if (raw == null || raw === '') return '';
  if (typeof raw === 'object' && raw != null) {
    if (raw.name != null) return String(raw.name);
    if (raw.code != null) return String(raw.code);
    return '';
  }
  return String(raw);
}

export function pdfCategoryRaw(p) {
  const raw = p.category;
  if (raw == null || raw === '') return '';
  if (typeof raw === 'object' && raw.name != null) return String(raw.name);
  return String(raw);
}

export function pdfFinishRaw(p) {
  let raw = p.finish ?? p.finishes ?? p.finish_type ?? p.finishType ?? null;
  if (raw == null || raw === '') return '';
  const formatted = formatNamedRelationList(raw);
  if (formatted) return formatted;
  if (typeof raw === 'object' && raw != null) {
    if (raw.name != null) return String(raw.name);
    if (raw.label != null) return String(raw.label);
  }
  return '';
}

export function pdfMaterialRaw(p) {
  let raw = p.material ?? p.materials ?? p.material_type ?? p.materialType ?? null;
  if (raw == null || raw === '') return '';
  const formatted = formatNamedRelationList(raw);
  if (formatted) return formatted;
  if (typeof raw === 'object' && raw != null) {
    if (raw.name != null) return String(raw.name);
    if (raw.label != null) return String(raw.label);
  }
  return '';
}

export function pdfColorRaw(p) {
  let raw = p.color ?? p.colors ?? p.color_type ?? p.colorType ?? null;
  if (raw == null || raw === '') return '';
  const formatted = formatNamedRelationList(raw);
  if (formatted) return formatted;
  if (typeof raw === 'object' && raw != null) {
    if (raw.name != null) return String(raw.name);
    if (raw.label != null) return String(raw.label);
  }
  return '';
}
